// Chat-timeline engine — the message list + typing/abort/schedule mechanics
// extracted from the monolith (addTyping/addInes/chainMessages/runStepGen/schedule).
// Generic over message `kind` so it knows nothing about the flow's steps; the
// orchestrator (useSignupFlow) decides which kinds to push and when. DOM-free
// (scrolling lives in the component), so it is fully unit-testable.
import { ref, type Ref } from 'vue'

// A timeline entry. `kind` discriminates the renderer (bot / user / typing / a step
// card like 'universe'); the remaining payload varies by kind.
export interface ChatMessage {
  id: number
  kind: string
  [key: string]: unknown
}

const TYPING_BASE_MS = 250
const TYPING_PER_CHAR_MS = 10
const TYPING_MAX_MS = 600
const INTER_BUBBLE_MS = 100

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
function stripHtml(s: string): string {
  return String(s).replace(/<[^>]+>/g, '')
}

export function useChatTimeline() {
  const messages: Ref<ChatMessage[]> = ref([])

  let idSeq = 0
  const mkId = () => ++idSeq

  // Abort generation: bumped on back-navigation so an in-flight chainMessages()
  // cancels instead of appending stale bubbles after the timeline was rewound.
  let gen = 0
  const bumpGen = () => ++gen
  const currentGen = () => gen
  const isAborted = (expected?: number) => expected !== undefined && expected !== gen

  // A single pending inter-step timer (cancellable on back-nav).
  let pendingTimer: ReturnType<typeof setTimeout> | null = null
  function clearPending(): void {
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer)
      pendingTimer = null
    }
  }
  function schedule(fn: () => void, delay = 350): void {
    clearPending()
    pendingTimer = setTimeout(() => {
      pendingTimer = null
      fn()
    }, delay)
  }

  function push(kind: string, payload: Record<string, unknown> = {}): number {
    const id = mkId()
    messages.value.push({ id, kind, ...payload })
    return id
  }
  const addBot = (content: string, { showAvatar = true }: { showAvatar?: boolean } = {}) =>
    push('bot', { content, showAvatar })
  const addUser = (
    content: string,
    { step = null, icon = null }: { step?: string | null; icon?: string | null } = {},
  ) => push('user', { content, editable: !!step, step, icon })
  const addTyping = () => push('typing')
  const addCard = (kind: string, extra: Record<string, unknown> = {}) => push(kind, extra)

  function removeById(id: number): void {
    const i = messages.value.findIndex((m) => m.id === id)
    if (i >= 0) messages.value.splice(i, 1)
  }
  function removeFirstOfKind(kind: string): void {
    const i = messages.value.findIndex((m) => m.kind === kind)
    if (i >= 0) messages.value.splice(i, 1)
  }
  function truncateFrom(index: number): void {
    if (index >= 0) messages.value.splice(index)
  }

  function reset(): void {
    clearPending()
    bumpGen()
    messages.value = []
  }

  // Type out a sequence of bot bubbles, one at a time (typing indicator → delay →
  // bubble). `quiet` skips the animation (used for instant back-navigation re-render).
  // `gen` is the caller's captured generation; if it changes mid-flight the chain aborts.
  async function chainMessages(
    msgs: string[],
    { gen: expected, quiet = false }: { gen?: number; quiet?: boolean } = {},
  ): Promise<void> {
    for (let i = 0; i < msgs.length; i++) {
      if (isAborted(expected)) return
      const msg = msgs[i]!
      if (!quiet) {
        const tid = addTyping()
        const delay = TYPING_BASE_MS + Math.min(stripHtml(msg).length * TYPING_PER_CHAR_MS, TYPING_MAX_MS)
        await sleep(delay)
        if (isAborted(expected)) {
          removeById(tid)
          return
        }
        removeById(tid)
      }
      addBot(msg, { showAvatar: i === msgs.length - 1 })
      if (i < msgs.length - 1 && !quiet) await sleep(INTER_BUBBLE_MS)
    }
  }

  return {
    messages,
    mkId,
    bumpGen,
    currentGen,
    isAborted,
    schedule,
    clearPending,
    addBot,
    addUser,
    addTyping,
    addCard,
    removeById,
    removeFirstOfKind,
    truncateFrom,
    reset,
    chainMessages,
  }
}
